import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import bankTransactionService from '../services'
import { TransactionQueryKeys } from '../../keys'
import {
	CreateManualWithdrawTransactionRequest,
	CreateWithdrawTransactionRequest,
} from '../../types'
import showErrorToast from '@/common/utils/showErrorToast'

export function useMutateWithdrawTransaction() {
	const queryClient = useQueryClient()

	const manualWithdraw = useMutation({
		mutationFn: async (id: string) => {
			await bankTransactionService.manualBankWithdraw(id)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [TransactionQueryKeys.WITHDRAW_TRANSACTION],
			})
			toast.success('ทำรายการสำเร็จ')
		},
		onError: (error) => {
			showErrorToast(error)
		},
	})

	const forceComplete = useMutation({
		mutationFn: async (params: { id: string; note?: string }) => {
			await bankTransactionService.forceCompleteWithdraw(params.id, {
				note: params.note,
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [TransactionQueryKeys.WITHDRAW_TRANSACTION],
			})
			toast.success('ทำรายการสำเร็จ')
		},
		onError: (error) => {
			showErrorToast(error)
		},
	})

	const cancel = useMutation({
		mutationFn: async (params: { id: string; note?: string }) => {
			await bankTransactionService.cancelWithdraw(params.id, {
				note: params.note,
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [TransactionQueryKeys.WITHDRAW_TRANSACTION],
			})
			toast.success('ยกเลิกรายการสำเร็จ')
		},
		onError: (error) => {
			showErrorToast(error)
		},
	})

	const createManualWithdraw = useMutation({
		mutationFn: async (data: CreateManualWithdrawTransactionRequest) => {
			await bankTransactionService.createManualBankWithdraw(data)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [TransactionQueryKeys.WITHDRAW_TRANSACTION],
			})
		},
	})

	const createWithdraw = useMutation({
		mutationFn: async (data: CreateWithdrawTransactionRequest) => {
			await bankTransactionService.createWithdraw(data)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [TransactionQueryKeys.WITHDRAW_TRANSACTION],
			})
		},
	})

	const approveWithdraw = useMutation({
		mutationFn: async (params: {
			id: string
			isManual?: boolean
			note?: string
		}) => {
			await bankTransactionService.approveWithdraw(params.id, {
				isManual: params.isManual,
				note: params.note,
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [TransactionQueryKeys.WITHDRAW_TRANSACTION],
			})
			toast.success('ทำรายการสำเร็จ')
		},
		onError: (error) => {
			showErrorToast(error)
		},
	})

	const refund = useMutation({
		mutationFn: async (params: { id: string; note?: string }) => {
			await bankTransactionService.refundWithdraw(params.id, {
				note: params.note,
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [TransactionQueryKeys.WITHDRAW_TRANSACTION],
			})
			toast.success('คืนเงินสำเร็จ')
		},
		onError: (error) => {
			showErrorToast(error)
		},
	})

	const retryWithdraw = useMutation({
		mutationFn: async (id: string) => {
			await bankTransactionService.retryWithdraw(id)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [TransactionQueryKeys.WITHDRAW_TRANSACTION],
			})
			toast.success('ลองอีกครั้งสำเร็จ')
		},
		onError: (error) => {
			showErrorToast(error)
		},
	})

	const updateStatus = useMutation({
		mutationFn: async (params: {
			id: string
			status: string
			note?: string
		}) => {
			await bankTransactionService.updateWithdrawStatus(params.id, {
				status: params.status,
				note: params.note,
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [TransactionQueryKeys.WITHDRAW_TRANSACTION],
			})
			toast.success('อัพเดทสถานะสำเร็จ')
		},
		onError: (error) => {
			showErrorToast(error)
		},
	})

	return {
		manualWithdraw,
		forceComplete,
		cancel,
		createManualWithdraw,
		createWithdraw,
		approveWithdraw,
		refund,
		retryWithdraw,
		updateStatus,
	}
}
